import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { CREW_ASSIGNMENTS, ProjectJob } from '../../data/projectsData';
import { TODAY, departmentColor, departmentLabel, formatCurrency } from '../../utils/projectsUtils';

interface CalendarOverviewProps {
  jobs: ProjectJob[];
  days: Date[];
  onSelectJob: (jobId: string) => void;
}

const CalendarOverview: React.FC<CalendarOverviewProps> = ({ jobs, days, onSelectJob }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const isoDays = useMemo(() => days.map((day) => format(day, 'yyyy-MM-dd')), [days]);
  const windowStart = isoDays[0];
  const windowEnd = isoDays[isoDays.length - 1];

  // Index crew counts by job+day for the visible window (single pass).
  const countsByJobDay = useMemo(() => {
    const index = new Map<string, number>();
    CREW_ASSIGNMENTS.forEach((assignment) => {
      if (!assignment.date || assignment.date < windowStart || assignment.date > windowEnd) return;
      const key = `${assignment.job_id}|${assignment.date}`;
      index.set(key, (index.get(key) || 0) + 1);
    });
    return index;
    // CREW_ASSIGNMENTS is a live binding that swaps when live data loads — recompute on that swap.
  }, [windowStart, windowEnd, CREW_ASSIGNMENTS]);

  const departments = useMemo(() => {
    const groups = new Map<string, ProjectJob[]>();
    jobs.forEach((job) => {
      if (!groups.has(job.dept_id)) groups.set(job.dept_id, []);
      groups.get(job.dept_id)!.push(job);
    });
    return Array.from(groups.entries()).sort((a, b) => departmentLabel(a[0]).localeCompare(departmentLabel(b[0])));
  }, [jobs]);

  const gridTemplate = `260px repeat(${days.length}, minmax(34px, 1fr))`;

  const toggle = (dept: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(dept) ? next.delete(dept) : next.add(dept);
      return next;
    });

  return (
    <Card variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720 }}>
        {/* Day header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplate, position: 'sticky', top: 0, zIndex: 2 }}>
          <Box sx={{ p: 1, backgroundColor: 'grey.200', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight={700}>
              Job / Customer
            </Typography>
          </Box>
          {days.map((day) => {
            const iso = format(day, 'yyyy-MM-dd');
            const isToday = iso === TODAY;
            const isWeekendDay = [0, 6].includes(day.getDay());
            return (
              <Box
                key={iso}
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  backgroundColor: isToday ? 'primary.100' : isWeekendDay ? 'grey.100' : 'grey.200',
                  borderBottom: 1,
                  borderLeft: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 10, display: 'block', lineHeight: 1.1 }}>
                  {format(day, 'EEE')}
                </Typography>
                <Typography variant="caption" fontWeight={isToday ? 700 : 500} sx={{ fontSize: 11 }}>
                  {format(day, 'M/d')}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Department groups */}
        {departments.map(([dept, deptJobs]) => {
          const color = departmentColor(dept);
          const isCollapsed = collapsed.has(dept);
          const deptTechDays = deptJobs.reduce(
            (sum, job) => sum + isoDays.reduce((s, iso) => s + (countsByJobDay.get(`${job.job_id}|${iso}`) || 0), 0),
            0,
          );
          return (
            <Box key={dept}>
              <Box
                onClick={() => toggle(dept)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  cursor: 'pointer',
                  backgroundColor: 'grey.50',
                  borderBottom: 1,
                  borderTop: 3,
                  borderTopColor: color,
                  borderBottomColor: 'divider',
                }}
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </IconButton>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: color }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {departmentLabel(dept)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {deptJobs.length} jobs · {deptTechDays} tech-days scheduled · {dept}
                </Typography>
              </Box>

              <Collapse in={!isCollapsed}>
                {deptJobs.map((job) => (
                  <Box
                    key={job.job_id}
                    onClick={() => onSelectJob(job.job_id)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplate,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{ p: 0.75, borderBottom: 1, borderColor: 'divider', borderLeft: 4, borderLeftColor: color }}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.2 }}>
                        {job.job_id}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 240,
                        }}
                      >
                        {job.company || job.description}
                      </Typography>
                    </Box>
                    {isoDays.map((iso) => {
                      const count = countsByJobDay.get(`${job.job_id}|${iso}`) || 0;
                      const intensity = count === 0 ? 0 : Math.min(1, 0.35 + count * 0.13);
                      return (
                        <Box
                          key={iso}
                          sx={{
                            borderBottom: 1,
                            borderLeft: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 30,
                          }}
                        >
                          {count > 0 && (
                            <Tooltip
                              arrow
                              title={`${job.job_id} · ${format(new Date(`${iso}T00:00:00`), 'EEE MMM d')} · ${count} ${
                                count === 1 ? 'technician' : 'technicians'
                              }`}
                            >
                              <Box
                                sx={{
                                  width: '85%',
                                  minHeight: 20,
                                  borderRadius: 0.5,
                                  backgroundColor: color,
                                  opacity: intensity,
                                  color: 'white',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {count}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Collapse>
            </Box>
          );
        })}

        {departments.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No jobs match the selected departments.
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default CalendarOverview;
